import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Trophy, Medal, Award, Star, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Ranking = () => {
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRanking();
  }, []);

  const fetchRanking = async () => {
    try {
      const response = await axios.get(`${API_URL}/trainings/ranking`);
      setRanking(response.data);
    } catch (error) {
      console.error('Error fetching ranking:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalIcon = (position) => {
    if (position === 1) return <Trophy size={14} className="text-yellow-500" />;
    if (position === 2) return <Medal size={14} className="text-gray-400" />;
    if (position === 3) return <Medal size={14} className="text-orange-600" />;
    return <Award size={12} className="text-slate-400" />;
  };

  const getBadgeColor = (badge) => {
    const colors = {
      'Primeiro Passo': 'bg-blue-500',
      'Dedicado': 'bg-purple-500',
      'Expert': 'bg-emerald-500',
      'Mestre SST': 'bg-red-500',
      '500 Pontos': 'bg-yellow-500',
      '1000 Pontos': 'bg-orange-500',
      'Líder de Segurança': 'bg-pink-500'
    };
    return colors[badge] || 'bg-slate-500';
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="ranking-page">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Ranking de Treinamentos
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Gamificação e conquistas dos funcionários
        </p>
      </div>

      {/* Top 3 Podium */}
      {ranking.length >= 3 && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          {/* 2nd Place */}
          <Card className="p-6 text-center transform translate-y-8">
            <div className="flex justify-center mb-3">
              {getMedalIcon(2)}
            </div>
            <h3 className="font-bold text-lg text-slate-900 dark:text-white">
              {ranking[1].employee.nome}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {ranking[1].employee.cargo}
            </p>
            <p className="text-3xl font-bold text-gray-400 mt-3">
              {ranking[1].pontos}
            </p>
            <p className="text-xs text-slate-500">pontos</p>
            <p className="text-sm text-slate-600 mt-2">
              {ranking[1].treinamentos_concluidos} treinamentos
            </p>
          </Card>

          {/* 1st Place */}
          <Card className="p-6 text-center bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950 dark:to-amber-950 border-2 border-yellow-500">
            <div className="flex justify-center mb-3">
              {getMedalIcon(1)}
            </div>
            <h3 className="font-bold text-xl text-slate-900 dark:text-white">
              {ranking[0].employee.nome}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {ranking[0].employee.cargo}
            </p>
            <p className="text-4xl font-bold text-yellow-500 mt-3">
              {ranking[0].pontos}
            </p>
            <p className="text-xs text-slate-500">pontos</p>
            <p className="text-sm text-slate-600 mt-2">
              {ranking[0].treinamentos_concluidos} treinamentos
            </p>
            <div className="flex flex-wrap gap-1 justify-center mt-3">
              {ranking[0].badges.map((badge) => (
                <Badge key={badge} className={getBadgeColor(badge)}>
                  {badge}
                </Badge>
              ))}
            </div>
          </Card>

          {/* 3rd Place */}
          <Card className="p-6 text-center transform translate-y-8">
            <div className="flex justify-center mb-3">
              {getMedalIcon(3)}
            </div>
            <h3 className="font-bold text-lg text-slate-900 dark:text-white">
              {ranking[2].employee.nome}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {ranking[2].employee.cargo}
            </p>
            <p className="text-3xl font-bold text-orange-600 mt-3">
              {ranking[2].pontos}
            </p>
            <p className="text-xs text-slate-500">pontos</p>
            <p className="text-sm text-slate-600 mt-2">
              {ranking[2].treinamentos_concluidos} treinamentos
            </p>
          </Card>
        </div>
      )}

      {/* Full Ranking List */}
      <div className="space-y-3">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
          Ranking Completo
        </h3>
        {ranking.map((item) => (
          <Card
            key={item.employee.id}
            className={`p-6 ${item.posicao <= 3 ? 'border-2 border-emerald-500' : ''}`}
            data-testid={`rank-${item.posicao}`}
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-10 text-center">
                {item.posicao <= 3 ? (
                  getMedalIcon(item.posicao)
                ) : (
                  <div className="text-xl font-bold text-slate-400">#{item.posicao}</div>
                )}
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-bold text-slate-900 dark:text-white">{item.employee.nome}</h4>
                  {item.posicao === 1 && (
                    <Badge className="bg-yellow-500">
                      <Star size={12} className="mr-1" />
                      Líder
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {item.employee.cargo} - {item.employee.setor}
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {item.badges.map((badge) => (
                    <Badge key={badge} className={`text-xs ${getBadgeColor(badge)}`}>
                      {badge}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="text-right">
                <p className="text-3xl font-bold text-emerald-500">{item.pontos}</p>
                <p className="text-xs text-slate-500">pontos</p>
                <div className="flex items-center gap-1 justify-end mt-2 text-sm text-slate-600">
                  <TrendingUp size={14} />
                  <span>{item.treinamentos_concluidos} treinamentos</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {ranking.length === 0 && (
        <Card className="p-12 text-center">
          <Trophy size={20} className="mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 dark:text-slate-400">
            Nenhum treinamento concluído ainda. Complete treinamentos para aparecer no ranking!
          </p>
        </Card>
      )}

      {/* Badges Info */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
        <h4 className="font-bold text-slate-900 dark:text-white mb-3">
          🏆 Sistema de Conquistas
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-slate-900 p-3 rounded-lg">
            <Badge className="bg-blue-500 mb-2">Primeiro Passo</Badge>
            <p className="text-xs text-slate-600 dark:text-slate-400">Complete 1 treinamento</p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 rounded-lg">
            <Badge className="bg-purple-500 mb-2">Dedicado</Badge>
            <p className="text-xs text-slate-600 dark:text-slate-400">Complete 5 treinamentos</p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 rounded-lg">
            <Badge className="bg-emerald-500 mb-2">Expert</Badge>
            <p className="text-xs text-slate-600 dark:text-slate-400">Complete 10 treinamentos</p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 rounded-lg">
            <Badge className="bg-red-500 mb-2">Mestre SST</Badge>
            <p className="text-xs text-slate-600 dark:text-slate-400">Complete 20 treinamentos</p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 rounded-lg">
            <Badge className="bg-yellow-500 mb-2">500 Pontos</Badge>
            <p className="text-xs text-slate-600 dark:text-slate-400">Acumule 500 pontos</p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 rounded-lg">
            <Badge className="bg-orange-500 mb-2">1000 Pontos</Badge>
            <p className="text-xs text-slate-600 dark:text-slate-400">Acumule 1000 pontos</p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 rounded-lg">
            <Badge className="bg-pink-500 mb-2">Líder de Segurança</Badge>
            <p className="text-xs text-slate-600 dark:text-slate-400">Acumule 2000 pontos</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Ranking;
